import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { featureDetails } from './AIFeatureDetailPage';
import DemoBookingModal from '../components/DemoBookingModal';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';

// Custom hook for setting page metadata (React 19 compatible)
function usePageMeta({ title, description, ogTitle, ogDescription, ogUrl }) {
  useEffect(() => {
    // Set document title
    if (title) document.title = title;
    
    // Set meta tags
    const setMeta = (name, content, property = false) => {
      if (!content) return;
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };
    
    setMeta('description', description);
    setMeta('og:title', ogTitle, true);
    setMeta('og:description', ogDescription, true);
    setMeta('og:url', ogUrl, true);
    setMeta('og:type', 'website', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', ogTitle);
  }, [title, description, ogTitle, ogDescription, ogUrl]);
}

const TRUST_STATS = [
  { num: '500+', label: { en: 'Schools Trust Us', hi: 'स्कूल हम पर भरोसा करते हैं' } },
  { num: '50,000+', label: { en: 'Students Benefited', hi: 'विद्यार्थी लाभान्वित' } },
  { num: '31', label: { en: 'AI Features', hi: 'AI Features' } },
  { num: '98%', label: { en: 'Satisfaction Rate', hi: 'संतुष्टि दर' } },
];

const HERO_IMAGES = {
  admin: 'https://images.unsplash.com/photo-1709290749293-c6152a187b14?auto=format&fit=crop&w=1200&q=80',
  teacher: 'https://images.pexels.com/photos/8423008/pexels-photo-8423008.jpeg?auto=compress&cs=tinysrgb&w=1200',
  student: 'https://images.pexels.com/photos/7683899/pexels-photo-7683899.jpeg?auto=compress&cs=tinysrgb&w=1200',
  parent: 'https://images.unsplash.com/photo-1763013259158-8a8370542ddb?auto=format&fit=crop&w=1200&q=80',
  all: 'https://images.unsplash.com/photo-1756885375569-f04400d99cee?auto=format&fit=crop&w=1200&q=80',
};

const GRADIENTS = {
  admin: 'from-purple-600 via-indigo-600 to-blue-700',
  teacher: 'from-blue-600 via-cyan-600 to-teal-600',
  student: 'from-green-500 via-emerald-600 to-teal-600',
  parent: 'from-orange-500 via-amber-500 to-yellow-500',
  all: 'from-orange-500 via-red-500 to-pink-500',
};

const CATEGORY_LABELS = {
  admin: { en: 'For School Admins', hi: 'स्कूल एडमिन के लिए' },
  teacher: { en: 'For Teachers', hi: 'शिक्षकों के लिए' },
  student: { en: 'For Students', hi: 'विद्यार्थियों के लिए' },
  parent: { en: 'For Parents', hi: 'अभिभावकों के लिए' },
  all: { en: 'For Everyone', hi: 'सभी के लिए' },
};

const PROBLEMS = {
  admin: { en: 'Managing school operations manually wastes hours daily', hi: 'स्कूल operations manually manage करना रोज़ाना घंटों बर्बाद करता है' },
  teacher: { en: 'Teachers spend more time on paperwork than teaching', hi: 'शिक्षक पढ़ाने से ज़्यादा समय paperwork में लगाते हैं' },
  student: { en: 'Students struggle without personalized learning support', hi: 'विद्यार्थी personalized learning support के बिना struggle करते हैं' },
  parent: { en: 'Parents have no visibility into their child\'s progress', hi: 'Parents को अपने बच्चे की progress की कोई visibility नहीं' },
  all: { en: 'Schools lack a unified digital platform', hi: 'स्कूलों में एक unified digital platform की कमी है' },
};

const FEATURE_ROUTE_ALIASES = {
  'notice-simplifier': 'simplify-notice',
  'progress-summary': 'progress-summary',
  'homework-hints': 'homework-hint',
  'mcq-generator': 'generate-mcq',
  'study-plan': 'study-plan',
  'faq-bot': 'faq',
  'fee-reminder': 'fee-reminder',
  'life-skills': 'life-skills',
  'complaint-reply': 'complaint-draft',
  'compliance-checklist': 'compliance-checklist',
  'chapter-explainer': 'chapter-explanation',
  'micro-tasks': 'micro-task',
  'homework-assistant': 'homework-revision',
  'format-adapter': 'format-adapter',
  'complaint-guidance': 'complaint-guidance',
  'content-improver': 'content-improver',
  'communication-templates': 'communication-template',
  'student-support': 'student-support',
  'workflow-reminders': 'workflow-reminder',
  'parent-portal': 'parent-portal-simplify',
  'weak-topics': 'class-weak-topics',
  'question-paper': 'question-paper-gen',
  'report-card': 'report-card-gen',
  'at-risk-alerts': 'at-risk-alerts',
  'child-progress': 'child-progress',
  'daily-summary': 'daily-summary',
  'attendance-alerts': 'attendance-alerts',
  'practice-tests': 'practice-tests',
  'gamified-learning': 'gamified-learning',
  'revision-scheduler': 'revision-scheduler',
  'weekly-challenge': 'weekly-challenge'
};

export default function FeatureMarketingPage() {
  const { featureId } = useParams();
  const { language } = useLanguage();
  const resolvedFeatureId = FEATURE_ROUTE_ALIASES[featureId] || featureId;
  const feature = featureDetails[resolvedFeatureId];
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Safely get feature properties with fallbacks (must be before hooks)
  const featureName = feature?.name?.en || 'Feature';
  const featureShortDesc = feature?.shortDesc?.en || '';
  const featureFullDesc = feature?.fullDesc?.en || '';
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const shareUrl = `${baseUrl}/features/${featureId}`;
  const pageTitle = String(featureName) + ' | Shiksha Mitra - AI School Management';
  const ogTitle = String(featureName) + ' | Shiksha Mitra';
  const ogDesc = String(featureFullDesc).substring(0, 160);

  // Set page metadata using React 19 compatible hook (must be called before any returns)
  usePageMeta({
    title: pageTitle,
    description: featureShortDesc,
    ogTitle: ogTitle,
    ogDescription: ogDesc,
    ogUrl: shareUrl
  });

  useEffect(() => { window.scrollTo(0, 0); }, [featureId]);

  if (!feature) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <span className="text-6xl block mb-4">🔍</span>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{language === 'hi' ? 'Feature नहीं मिला' : 'Feature Not Found'}</h2>
          <Link to="/features" className="px-6 py-3 bg-orange-500 text-white rounded-lg" data-testid="back-to-features">{language === 'hi' ? 'सभी Features देखें' : 'View All Features'}</Link>
        </div>
      </div>
    );
  }

  const gradient = GRADIENTS[feature.category] || GRADIENTS.all;
  const catLabel = CATEGORY_LABELS[feature.category] || CATEGORY_LABELS.all;
  const problem = PROBLEMS[feature.category] || PROBLEMS.all;
  const shareText = `${feature.name[language]} - ${feature.shortDesc[language]} | शिक्षा मित्र`;

  return (
    <div className="min-h-screen bg-white" data-testid="feature-marketing-page">

      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="marketing-header-logo">
            <span className="text-xl font-bold text-orange-600">शिक्षा मित्र</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/features" className="text-sm text-gray-600 hover:text-orange-600 hidden sm:inline">{language === 'hi' ? 'सभी Features' : 'All Features'}</Link>
            <button onClick={() => setShowDemoModal(true)} className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600 transition" data-testid="marketing-cta-top">
              {language === 'hi' ? 'Free Demo' : 'Free Demo'}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className={`bg-gradient-to-br ${gradient} text-white py-12 sm:py-20 relative overflow-hidden`} data-testid="hero-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left - Text */}
            <div className="text-center md:text-left">
              <span className="inline-block px-4 py-1.5 bg-white/20 backdrop-blur rounded-full text-sm mb-5">
                {catLabel[language]} • शिक्षा मित्र AI
              </span>
              <div className="text-5xl mb-4">{feature.icon}</div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight mb-4" data-testid="hero-title">
                {feature.name[language]}
              </h1>
              <p className="text-base sm:text-lg opacity-90 max-w-lg mb-8">
                {feature.shortDesc[language]}
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <button onClick={() => setShowDemoModal(true)} className="px-8 py-3.5 bg-white text-gray-800 font-bold rounded-xl hover:bg-gray-100 transition shadow-lg" data-testid="hero-cta-demo">
                  {language === 'hi' ? 'Free Demo बुक करें' : 'Book Free Demo'}
                </button>
                <button onClick={() => setShowPaymentModal(true)} className="px-8 py-3.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition shadow-lg" data-testid="hero-cta-payment">
                  {language === 'hi' ? 'Payment से Start करें' : 'Start with Payment'}
                </button>
                <a href="#how-it-works" className="px-8 py-3.5 bg-white/20 backdrop-blur text-white font-bold rounded-xl hover:bg-white/30 transition border border-white/30">
                  {language === 'hi' ? 'कैसे काम करता है?' : 'How it Works?'}
                </a>
              </div>
            </div>
            {/* Right - Image */}
            <div className="hidden md:block">
              <div className="relative">
                <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-xl"></div>
                <img
                  src={HERO_IMAGES[feature.category] || HERO_IMAGES.all}
                  alt={feature.name.en}
                  className="relative rounded-2xl shadow-2xl w-full h-72 lg:h-80 object-cover border-4 border-white/20"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-3 -right-3 bg-white text-gray-800 px-4 py-2 rounded-xl shadow-lg text-sm font-bold">
                  {feature.icon} AI Powered
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-gray-900 py-5">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {TRUST_STATS.map((s, i) => (
            <div key={i}>
              <p className="text-2xl font-extrabold text-white">{s.num}</p>
              <p className="text-xs text-gray-400">{s.label[language]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Problem → Solution */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Problem */}
            <div className="bg-red-50 border border-red-200 rounded-2xl p-8" data-testid="problem-section">
              <span className="text-3xl block mb-3">⚠</span>
              <h3 className="text-xl font-bold text-red-800 mb-3">{language === 'hi' ? 'समस्या' : 'The Problem'}</h3>
              <p className="text-red-700">{problem[language]}</p>
              <ul className="mt-4 space-y-2">
                {(feature.limitations[language] || []).slice(0, 3).map((l, i) => (
                  <li key={i} className="text-sm text-red-600 flex items-start gap-2">
                    <span className="text-red-400 mt-0.5">✗</span> {l}
                  </li>
                ))}
              </ul>
            </div>
            {/* Solution */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8" data-testid="solution-section">
              <span className="text-3xl block mb-3">{feature.icon}</span>
              <h3 className="text-xl font-bold text-green-800 mb-3">{language === 'hi' ? 'समाधान' : 'The Solution'}</h3>
              <p className="text-green-700">{feature.fullDesc[language]}</p>
              <ul className="mt-4 space-y-2">
                {(feature.benefits[language] || []).slice(0, 3).map((b, i) => (
                  <li key={i} className="text-sm text-green-600 flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">✓</span> {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-3" data-testid="how-it-works-title">
            {language === 'hi' ? 'कैसे काम करता है?' : 'How it Works?'}
          </h2>
          <p className="text-gray-500 text-center mb-10 max-w-lg mx-auto">
            {language === 'hi' ? 'बस कुछ simple steps में शुरू करें' : 'Get started in just a few simple steps'}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(feature.howItWorks[language] || []).map((step, idx) => (
              <div key={idx} className="relative text-center group">
                <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${gradient} text-white flex items-center justify-center text-xl font-bold mb-4 group-hover:scale-110 transition`}>
                  {idx + 1}
                </div>
                {idx < (feature.howItWorks[language] || []).length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-0.5 bg-gray-200"></div>
                )}
                <p className="text-sm text-gray-700 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-10">
            {language === 'hi' ? 'प्रमुख लाभ' : 'Key Benefits'}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(feature.benefits[language] || []).map((b, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 shadow-sm border hover:shadow-md transition group" data-testid={`benefit-${idx}`}>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${gradient} text-white flex items-center justify-center mb-3 group-hover:scale-110 transition`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="text-gray-700">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-800 mb-10">
            {language === 'hi' ? 'कहाँ इस्तेमाल करें?' : 'Use Cases'}
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {(feature.useCases[language] || []).map((uc, idx) => (
              <span key={idx} className={`px-5 py-2.5 rounded-full text-sm font-medium bg-gradient-to-r ${gradient} text-white shadow-sm`}>
                {uc}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className={`py-16 bg-gradient-to-br ${gradient} text-white`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            {language === 'hi' ? '500+ स्कूल पहले से इस्तेमाल कर रहे हैं' : '500+ Schools Already Using This'}
          </h2>
          <p className="text-white/80 mb-8 max-w-lg mx-auto">
            {language === 'hi'
              ? 'CBSE, ICSE, और State Board स्कूलों में शिक्षा मित्र सबसे भरोसेमंद platform है।'
              : 'Shiksha Mitra is the most trusted platform in CBSE, ICSE, and State Board schools.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {['CBSE Schools', 'ICSE Schools', 'State Board', 'Private Schools', 'Play Schools'].map((badge, i) => (
              <span key={i} className="px-4 py-2 bg-white/20 backdrop-blur rounded-full text-sm">{badge}</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <span className="text-5xl block mb-4">{feature.icon}</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">
            {language === 'hi' ? 'आज ही शुरू करें!' : 'Get Started Today!'}
          </h2>
          <p className="text-gray-500 mb-8 max-w-lg mx-auto">
            {language === 'hi'
              ? `${feature.name.hi} और 30+ AI features के साथ अपने स्कूल को digital बनाएं। No setup fee, No hidden charges।`
              : `Digitize your school with ${feature.name.en} and 30+ AI features. No setup fee, no hidden charges.`}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => setShowDemoModal(true)} className="px-8 py-3.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition shadow-lg" data-testid="cta-bottom-demo">
              {language === 'hi' ? 'Free Demo बुक करें' : 'Book Free Demo'}
            </button>
            <button onClick={() => setShowPaymentModal(true)} className="px-8 py-3.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition shadow-lg" data-testid="cta-bottom-payment">
              {language === 'hi' ? 'Payment से Start करें' : 'Start with Payment'}
            </button>
            <Link to="/login" className="px-8 py-3.5 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:border-orange-500 hover:text-orange-500 transition" data-testid="cta-bottom-login">
              {language === 'hi' ? 'लॉगिन करें' : 'Login'}
            </Link>
          </div>
        </div>
      </section>

      {/* Share Buttons */}
      <ShareBar shareUrl={shareUrl} shareText={shareText} language={language} />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <span className="text-xl font-bold text-orange-400">शिक्षा मित्र</span>
              <p className="text-sm text-gray-400 mt-1">AI-Powered School Management Platform</p>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/features" className="hover:text-white transition">Features</Link>
              <Link to="/" className="hover:text-white transition">Home</Link>
              <Link to="/login" className="hover:text-white transition">Login</Link>
              <Link to="/privacy" className="hover:text-white transition">Privacy</Link>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-6">© 2025 System Of Success. All rights reserved.</p>
        </div>
      </footer>

      <DemoBookingModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} source={`feature_marketing_${featureId}`} />
      <SubscriptionPaymentModal isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} language={language} startStep={1} source={`feature_marketing_${featureId}_payment`} />
    </div>
  );
}

// Floating Share Bar
function ShareBar({ shareUrl, shareText, language }) {
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);
  const [copied, setCopied] = React.useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-white/95 backdrop-blur shadow-xl rounded-full border px-4 py-2 flex items-center gap-2" data-testid="share-bar">
      <span className="text-xs text-gray-500 mr-1 hidden sm:inline">{language === 'hi' ? 'Share:' : 'Share:'}</span>
      <a href={`https://wa.me/?text=${encodedText}%20${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center hover:scale-110 transition" data-testid="share-whatsapp" title="WhatsApp">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:scale-110 transition" data-testid="share-facebook" title="Facebook">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
      </a>
      <a href={`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center hover:scale-110 transition" data-testid="share-twitter" title="X/Twitter">
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      </a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-[#0A66C2] text-white flex items-center justify-center hover:scale-110 transition" data-testid="share-linkedin" title="LinkedIn">
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
      </a>
      <button onClick={copyLink} className={`w-8 h-8 rounded-full flex items-center justify-center hover:scale-110 transition ${copied ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-600'}`} data-testid="share-copy-link" title="Copy Link">
        {copied ? (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
        )}
      </button>
    </div>
  );
}
